<template>
  <!-- Whitelist -->
  <remastered-dialog
    :title="$t('whiteList.login')"
    :blurred="accessDeniedDialog"
  >
    <div
      v-if="error"
      class="error"
    >
      {{ $t('whiteList.error') }}
    </div>
    <el-form
      label-position="top"
      :model="form"
      :rules="rules"
    >
      <el-form-item
        prop="username"
        :label="$t('general.username')"
      >
        <el-input
          v-model="form.username"
          name="username"
          :placeholder="$t('general.username')"
        />
      </el-form-item>
      <el-form-item
        prop="password"
        :label="$t('general.password')"
      >
        <el-input
          v-model="form.password"
          name="password"
          :placeholder="$t('general.password')"
          show-password
          password
        />
      </el-form-item>
      <el-form-item class="login-helper">
        <div class="items">
          <el-checkbox v-model="isKeepLogin">
            {{ $t('vrlounge.keepLogin') }}
          </el-checkbox>
          <a
            :href="forgotPasswordUrl"
            target="_blank"
          >
            {{ $t('general.forgetPassword') }}
          </a>
        </div>
      </el-form-item>
      <el-form-item class="submit-button">
        <el-button
          :loading="loading"
          @click="handleSubmit"
        >
          {{ $t('general.login') }}
        </el-button>
      </el-form-item>
    </el-form>
    <template #after>
      <div
        class="dialog-denied"
        :class="{ visible: accessDeniedDialog }"
      >
        <div class="title">
          {{ $t('whiteList.warnTitle') }}
        </div>
        <div class="content">
          {{ $t('whiteList.warnContent') }}
        </div>
        <div class="actions">
          <el-button
            @click="accessDeniedDialog = false"
          >
            {{ $t('confirm') }}
          </el-button>
        </div>
      </div>
    </template>
  </remastered-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import RemasteredDialog from '@/components/common/RemasteredDialog'
import webStorage from '../../../store/webStorage/helper'

export default {
  components: {
    RemasteredDialog
  },
  data () {
    return {
      form: {
        username: '',
        password: ''
      },
      rules: {
        username: [
          {
            required: true,
            message: this.$t('general.required')
          }
        ],
        password: [
          {
            required: true,
            min: 6,
            message: this.$t('general.required')
          }
        ]
      },
      isKeepLogin: true,
      editorUrl: process.env.EDITOR_URL,
      accessDeniedDialog: false,
      error: false,
      loading: false
    }
  },
  computed: {
    ...mapGetters([
      'currentBuilding',
      'property'
    ]),
    forgotPasswordUrl () {
      const hasProperty = this.property && this.property.objectId
      let redirectUrl = ''
      if (hasProperty) {
        redirectUrl = this.property.meta.viewerRedirectUrl || location.href
      } else {
        redirectUrl = this.currentBuilding.meta.viewerRedirectUrl || location.href
      }
      return `${this.editorUrl}/forgot-pass?redirectUrl=${encodeURIComponent(redirectUrl)}`
    }
  },
  watch: {
    isKeepLogin (val) {
      webStorage.setItem(`keep_login_${process.env.NODE_ENV}`, JSON.stringify(val))
    }
  },
  created () {
    this.isKeepLogin = JSON.parse(webStorage.getItem(`keep_login_${process.env.NODE_ENV}`) || 'true')
  },
  methods: {
    ...mapActions([
      'closeVisitor'
    ]),
    ...mapActions('auth', [
      'login'
    ]),
    ...mapActions('visitorHistory', {
      startDetailViewSession: 'startViewSession'
    }),
    async handleSubmit () {
      this.error = false
      this.loading = true
      try {
        const inWhitelist = await this.userInWhitelist()
        if (!inWhitelist) {
          this.accessDeniedDialog = true
          return
        }
        const result = await this.login({
          username: this.form.username,
          password: this.form.password,
          isKeepLogin: this.isKeepLogin
        })
        const profile = {
          name: result.name || '',
          email: String(result.email || result.username).toLowerCase(),
          phone: result.phone || ''
        }
        this.$mixpanel.setPeopleInfo(profile)
        this.$mixpanel.start()
        /* Detail visitor tracking - start session on logined & verification passed */
        this.startDetailViewSession(this.currentBuilding.objectId)
        this.closeVisitor()
        this.$emit('callback', result)
      } catch (error) {
        console.log(error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    userInWhitelist () {
      const result = this.currentBuilding.whiteList.includes(this.form.username)
      // Simulate loading state for better UX
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(result)
        }, 500)
      })
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~css/addon/main'

.error {
  color: $pink-color
  letter-spacing: 1px
  margin-bottom: 20px
  font-size: 14px
  font-weight: bold
}
.el-form-item {
  margin-bottom: 30px

  @media (max-width: $response-xs) {
    margin-bottom: 35px
  }
  &:last-child {
    margin-bottom: 0
  }
  >>> {
    .el-form-item__label {
      line-height: 1
      padding-bottom: 5px
      letter-spacing: 1px

      &:before {
        display: none
      }
    }
    .el-input__inner {
      border-color: rgba($pink-color, .3)

      &:focus {
        border-color: $pink-color
      }
    }
  }
}
.login-helper {
  margin-top: -5px

  >>> {
    .el-checkbox__input.is-focus .el-checkbox__inner,
    .el-checkbox__inner:hover {
      border-color: $pink-color
    }
    .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: $pink-color
      border-color: $pink-color
    }
    .el-checkbox__label {
      color: #606266
    }
    .el-form-item__content {
      line-height: 1
    }
  }
  .items {
    display: flex
    justify-content: space-between
  }
  a {
    color: $vrl-primary-text
    text-decoration: underline
  }
}
.submit-button {

  >>> {
    .el-form-item__content {
      display: flex
      justify-content: center
    }
    .el-button {
      padding: 12px 60px
      font-size: 16px

      @media (max-width: $response-xs) {
        padding: 14px 70px
      }
    }
  }
}
.dialog-denied {
  position: absolute
  width: 360px
  max-width: calc(100% - 20px)
  padding: 30px
  background: #FFF
  text-align: center
  border: 3px solid $pink-color
  box-shadow: 0 0 50px rgba(#000, .5)
  border-radius: 15px
  transform: scale(1.1)
  opacity: 0
  transition: all .15s ease-in-out
  z-index: 1
  visibility: hidden

  &.visible {
    transform: scale(1)
    opacity: 1
    visibility: visible
  }
  .title {
    font-weight: bold
    font-size: 18px
    color: $pink-color
  }
  .content {
    margin-top: 15px
    line-height: 20px
  }
  .actions {
    display: flex
    justify-content: center

    button {
      margin-top: 15px
      padding: 10px 20px
    }
  }
}
</style>
